"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { Play } from "lucide-react";
import { motion } from "framer-motion";
import GalleryModal, {
  MediaItem,
} from "@/components/Layouts/components/GalleryModal";
import VideoModal from "@/components/Layouts/components/VideoModal";

export interface DayInLifeTimeCard {
  id: string;
  time: string;
  subtag: string;
  title: string;
  footer: string;
  image: string;
  videourl?: string;
  videoUrl?: string;
  images?: string[];
  gallery?: MediaItem[];
  picture?: string;
  picture_2?: string;
  picture_3?: string;
  picture_4?: string;
  picture_5?: string;
  picture_6?: string;
}

const getAutoplayUrl = (url: string) => {
  if (!url) return "";
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    const videoIdMatch = url.match(
      /(?:embed\/|v=|v\/|vi\/|youtu\.be\/|\/v\/)([^#&?]*)/,
    );
    const videoId = videoIdMatch ? videoIdMatch[1] : "";
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&playsinline=1&enablejsapi=1`;
    }
  }
  return url;
};

export interface DayInLifeTabItem {
  id: string;
  label: string;
  active?: boolean;
  gallery?: MediaItem[];
}

export interface DayInLifeData {
  tag?: string;
  title?: string;
  description?: string;
  video?: {
    label?: string;
    title?: string;
    duration?: string;
    url?: string;
    media?: MediaItem[];
  };
  filterLabel?: string;
  tabs?: DayInLifeTabItem[];
  cards?: DayInLifeTimeCard[];
  scrollNote?: string;
}

interface DayInLifeSectionProps {
  data: DayInLifeData;
  onTabSelect?: (tabId: string) => void;
  onVideoClick?: () => void;
}

export default function DayInLifeSection({
  data,
  onTabSelect,
  onVideoClick,
}: DayInLifeSectionProps) {
  if (!data) return null;

  const {
    tag = "",
    title = "",
    description = "",
    video,
    filterLabel = "",
    tabs = [],
    cards = [],
    scrollNote = "VUỐT / KÉO ĐỂ KHÁM PHÁ MỘT NGÀY SỐNG",
  } = data;

  if (!title && tabs.length === 0 && cards.length === 0) {
    return null;
  }

  // Gallery Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMedia, setModalMedia] = useState<MediaItem[]>([]);
  const [modalIndex, setModalIndex] = useState(0);

  // Dedicated Video Modal State
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");

  // Active Tab state
  const [, setActiveTabId] = useState<string>(
    tabs.find((t) => t.active)?.id || tabs[0]?.id || "01",
  );

  // Drag Motion Values & Constraints
  const [maxDrag, setMaxDrag] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardsRowRef = useRef<HTMLDivElement>(null);

  // Update max drag distance on mount/resize
  useEffect(() => {
    const updateBounds = () => {
      if (cardsRowRef.current && containerRef.current) {
        const rowWidth = cardsRowRef.current.scrollWidth;
        const containerWidth = containerRef.current.clientWidth;
        const calculatedMax = Math.max(0, rowWidth - containerWidth);
        setMaxDrag(calculatedMax);
      }
    };

    updateBounds();
    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, [cards]);

  // Open Video Modal (Part 1)
  const handleOpenVideoModal = () => {
    if (!video) return;
    const urlToPlay =
      video.url ||
      (video.media && video.media.find((m) => m.type === "video")?.src) ||
      "";

    if (urlToPlay) {
      setVideoUrl(urlToPlay);
      setVideoTitle(video.title || video.label || "Brand Film");
      setVideoModalOpen(true);
    }

    if (onVideoClick) {
      onVideoClick();
    }
  };

  // Open Tab Gallery Modal (Part 2)
  const handleTabClick = (tab: DayInLifeTabItem) => {
    setActiveTabId(tab.id);

    const tabGalleryMedia: MediaItem[] =
      tab.gallery && tab.gallery.length > 0
        ? tab.gallery
        : [
            {
              type: "image",
              src: "/images/phoi_canh_1.png",
              title: tab.label,
            },
          ];

    setModalMedia(tabGalleryMedia);
    setModalIndex(0);
    setModalOpen(true);

    if (onTabSelect) {
      onTabSelect(tab.id);
    }
  };

  // Open Card Gallery Modal (Part 3)
  const handleCardClick = (card: DayInLifeTimeCard) => {
    let cardMedia: MediaItem[] = [];

    if (card.gallery && card.gallery.length > 0) {
      cardMedia = card.gallery;
    } else if (card.images && card.images.length > 0) {
      cardMedia = card.images.map((img) => ({
        type: "image",
        src: img,
        title: card.title
          ? `${card.time ? card.time + " - " : ""}${card.title.replace(/\n/g, " ")}`
          : card.subtag || "",
      }));
    } else {
      const pics = [
        card.picture,
        card.picture_2,
        card.picture_3,
        card.picture_4,
        card.picture_5,
        card.picture_6,
      ].filter((p): p is string =>
        Boolean(p && typeof p === "string" && p.trim() !== ""),
      );

      const allPics = pics.length > 0 ? pics : card.image ? [card.image] : [];
      const uniquePics = Array.from(new Set(allPics));

      const titleText = card.title
        ? `${card.time ? card.time + " - " : ""}${card.title.replace(/\n/g, " ")}`
        : card.subtag || "";

      cardMedia = uniquePics.map((src) => ({
        type: "image",
        src,
        title: titleText,
      }));
    }

    if (cardMedia.length > 0) {
      setModalMedia(cardMedia);
      setModalIndex(0);
      setModalOpen(true);
    }
  };

  const sectionRef = useRef<HTMLElement>(null);

  // Auto-pause videos when section is out of view, resume when back in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!sectionRef.current) return;
        const videos = sectionRef.current.querySelectorAll("video");
        videos.forEach((vid) => {
          if (!entry.isIntersecting) {
            vid.pause();
          } else {
            vid.play().catch(() => {});
          }
        });
      },
      { threshold: 0.15 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      id="mot-ngay-song"
      ref={sectionRef}
      className="relative w-full h-screen min-h-screen max-h-screen flex flex-col justify-between overflow-hidden bg-[#F6F3ED] text-[#193B2F] select-none px-6 sm:px-10 lg:px-14 xl:px-16 pt-16 sm:pt-20 md:pt-22 lg:pt-24 pb-6 md:pb-8"
    >
      {/* FULL SECTION BACKGROUND IMAGE */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Image
          src="/images/background_2.png"
          alt="Section Background"
          fill
          sizes="100vw"
          priority
          className="object-cover object-center"
        />
      </div>

      <div className="relative z-10 w-full h-full flex flex-col justify-between overflow-hidden gap-1">
        {/* ================= PHẦN 1 & PHẦN 2: HEADER & XEM MẶT BẰNG TIỆN ÍCH ================= */}
        <div className="w-full flex flex-col lg:flex-row justify-between items-end gap-3 lg:gap-6 flex-shrink-0">
          {/* LEFT COLUMN: TAG, MAIN TITLE & FLOOR PLAN BUTTONS (PART 2) */}
          <div className="w-full lg:w-[58%] space-y-3">
            <div className="space-y-1.5">
              <div className="flex items-center gap-3">
                <span className="w-7 h-[1px] bg-[#8A724C]" />
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#8A724C] font-sans">
                  {tag}
                </span>
              </div>

              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-[#193B2F] tracking-tight title-leading"
                style={{ lineHeight: 1.15 }}
              >
                {renderTextWithBreaks(title)}
              </h2>
            </div>

            {/* PHẦN 2: XEM MẶT BẰNG TIỆN ÍCH */}
            <div className="w-full flex items-center gap-2.5 sm:gap-3.5 flex-wrap pt-1">
              <span className="text-[10px] md:text-xs font-sans font-bold tracking-[0.18em] text-[#8A724C] uppercase shrink-0 mr-1">
                {filterLabel}
              </span>

              {tabs.map((tab) => {
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabClick(tab)}
                    className="group px-3.5 sm:px-4.5 py-1.5 rounded-full text-xs font-sans font-medium tracking-wide flex items-center gap-2 transition-all duration-300 cursor-pointer focus:outline-none bg-transparent text-[#193B2F] border border-[#193B2F]/30 hover:bg-[#193B2F] hover:text-white hover:border-[#193B2F]"
                  >
                    <span>{tab.label}</span>
                    <Image
                      src="/icons/arrow_outward.svg"
                      alt="arrow"
                      width={12}
                      height={12}
                      className="w-3 h-3 object-contain transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:invert group-hover:brightness-200"
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT COLUMN: DESCRIPTION & BRAND FILM BUTTON (ALIGNED TO BOTTOM OF PART 2) */}
          <div className="w-full lg:w-[38%] flex flex-col items-end lg:items-end text-left lg:text-right space-y-2 lg:pb-0.5">
            <p className="text-xs font-light font-sans text-[#4A5852] leading-relaxed max-w-md text-justify lg:text-right line-clamp-3 lg:line-clamp-none">
              {renderTextWithBreaks(description)}
            </p>

            {/* BRAND FILM VIDEO BUTTON (OPENS GALLERY MODAL) */}
            {video && (video.title || video.label) && (
              <div
                onClick={handleOpenVideoModal}
                className="inline-flex items-center gap-2.5 cursor-pointer group pt-0.5"
              >
                <div className="w-8 h-8 rounded-full bg-[#193B2F] text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform shrink-0">
                  <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-[9px] font-sans font-bold tracking-[0.16em] text-[#193B2F] uppercase leading-tight">
                    {video.label || "XEM BRAND FILM"}
                  </span>
                  <span className="text-xs font-lora font-normal text-[#193B2F] group-hover:text-[#8A724C] transition-colors leading-tight">
                    {video.title}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ================= PHẦN 3: TIMELINE TRONG NGÀY (UNIFIED PHYSICALLY ATTACHED CARDS + TIMELINE LINE) ================= */}
        <div className="w-full flex-1 min-h-0 flex flex-col justify-between overflow-hidden mt-4 md:mt-6 lg:mt-7 mb-1">
          {/* CARDS & TIMELINE UNIFIED OUTER DRAGGABLE CONTAINER */}
          <div
            ref={containerRef}
            className="w-full flex-1 min-h-0 overflow-hidden relative cursor-grab active:cursor-grabbing"
          >
            <motion.div
              ref={cardsRowRef}
              drag="x"
              dragConstraints={{ left: -maxDrag, right: 0 }}
              dragElastic={0.08}
              className="flex flex-col justify-between h-full w-max"
            >
              {/* 1. CARDS ROW */}
              <div className="flex gap-4.5 items-stretch flex-1 min-h-0">
                {cards.map((card) => {
                  const videoUrl = card.videourl || card.videoUrl || "";
                  const hasVideo = Boolean(videoUrl && videoUrl.trim() !== "");

                  return (
                    <div
                      key={card.id}
                      onClick={() => handleCardClick(card)}
                      className="w-[250px] sm:w-[270px] md:w-[290px] lg:w-[310px] xl:w-[330px] h-full min-h-[240px] relative overflow-hidden group border border-[#193B2F]/10 shadow-md flex-shrink-0 select-none cursor-pointer"
                    >
                      {/* Background Image or Video */}
                      {hasVideo ? (
                        videoUrl.includes("youtube.com") ||
                        videoUrl.includes("youtu.be") ? (
                          <iframe
                            src={getAutoplayUrl(videoUrl)}
                            title={card.title}
                            className="w-full h-full object-cover pointer-events-none scale-125 border-none"
                            allow="autoplay; fullscreen"
                          />
                        ) : (
                          <video
                            src={videoUrl}
                            poster={card.image}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover object-center pointer-events-none"
                          />
                        )
                      ) : (
                        <SmoothImage
                          src={card.image || "/images/phoi_canh_1.png"}
                          alt={card.title}
                          fill
                          sizes="auto"
                          priority
                          draggable={false}
                          className="object-cover object-center transition-transform duration-700 group-hover:scale-105 pointer-events-none"
                        />
                      )}

                      {/* Linear Gradient Overlay (Dark bottom fading out to top for text legibility) */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none z-10" />

                      {/* Card Content Overlay */}
                      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 z-20 flex flex-col justify-end text-white h-full pointer-events-none">
                        <div className="mt-auto space-y-1.5">
                          {/* Time Counter */}
                          <div className="font-lora text-2xl sm:text-3xl lg:text-3xl font-normal text-white leading-none">
                            {card.time}
                          </div>

                          {/* Subtag */}
                          <div className="text-[9px] sm:text-[10px] font-sans font-bold tracking-widest text-white/80 uppercase pt-0.5">
                            {card.subtag}
                          </div>

                          {/* Card Title */}
                          <h3 className="font-lora text-sm sm:text-base lg:text-lg font-normal text-white leading-snug line-clamp-2">
                            {renderTextWithBreaks(card.title)}
                          </h3>

                          {/* Card Footer Note */}
                          <div className="text-[8.5px] sm:text-[9px] font-sans font-semibold tracking-wider text-white/60 uppercase border-t border-white/20 pt-2 mt-2 block truncate">
                            {card.footer}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 2. TIMELINE LINE ROW */}
              <div className="w-full pt-4 pb-1 text-[#8A724C] flex-shrink-0">
                <div className="relative w-full flex items-center h-4 select-none">
                  {/* Continuous Track Line across entire cards width */}
                  <div className="absolute left-0 right-0 h-[1.5px] bg-[#8A724C]/40 pointer-events-none" />

                  {/* Dots positioned at the exact center of each card item above */}
                  <div className="flex gap-4.5 w-full items-center">
                    {cards.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => handleCardClick(c)}
                        className="w-[250px] sm:w-[270px] md:w-[290px] lg:w-[310px] xl:w-[330px] flex-shrink-0 flex flex-col items-center relative cursor-pointer"
                      >
                        {/* Solid Single-Color Dot at center of each card */}
                        <div className="w-2 h-2 rounded-full bg-[#8A724C] z-10 hover:scale-150 transition-transform" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* STATIC BOTTOM-LEFT ANCHORED INSTRUCTION NOTE */}
          <div className="w-full pt-1.5 flex-shrink-0">
            <span className="text-[8.5px] sm:text-[9.5px] font-sans font-bold tracking-[0.18em] text-[#8A724C] uppercase shrink-0 block">
              {scrollNote}
            </span>
          </div>
        </div>
      </div>

      {/* REUSABLE GALLERY MODAL */}
      <GalleryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        media={modalMedia}
        initialIndex={modalIndex}
      />

      {/* DEDICATED VIDEO MODAL */}
      <VideoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoUrl={videoUrl}
        title={videoTitle}
      />
    </section>
  );
}
